import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

import { endOfMonth, format, startOfMonth } from 'date-fns';

import { DEFAULT_PERIOD_PRESET } from '../helpers/reportPeriodPresets';
import { columnsForPreset, DEFAULT_COLUMN_PRESET } from '../types/ReportColumns';
import { FORMAT_BUDGET_CODE } from '../types/ReportFormat';
import { DEFAULT_LEVEL, LEVEL_FULL_DETAILS, levelToReportType } from '../types/ReportType';

import { apiClient } from 'Helpers/network';

import { useToast } from 'Hooks/useToast';

const DEFAULT_CRITERIA = {
    format: FORMAT_BUDGET_CODE,
    level: DEFAULT_LEVEL,
    departments: [],
    all_departments: 'all_departments',
    costcenters: {},
    show_workforce_details: false,
    // IB-834: include unallocated/unreconciled imported actuals. Default ON.
    include_unallocated: true,
    // Optional budget-version to compare against ('' = no comparison). Only
    // engaged by the backend at the Department + Cost Centre level.
    comparison_budget_id: '',
    name: '',
};

const ActualVsBudgetReportContext = createContext();

export const useActualVsBudgetReportContext = () => {
    const context = useContext(ActualVsBudgetReportContext);

    if (!context) {
        throw new Error('useActualVsBudgetReportContext must be used within a ActualVsBudgetReportContext');
    }

    return context;
};

export const useOptionalActualVsBudgetReportContext = () => {
    return useContext(ActualVsBudgetReportContext) || {};
};

export const ActualVsBudgetReportProvider = ({
    children,
    module,
    fetchConfigsUrl,
    actualCostsCreateUrl,
    generateReportUrl,
    previewCachedUrl,
    previewRefreshUrl,
    reportConfigsIndexUrl,
    reportConfigsStoreUrl,
    reportConfigsDeleteUrl,
    onReportGenerated,
}) => {
    const [configs, setConfigs] = useState({});
    const [criteria, setCriteria] = useState(DEFAULT_CRITERIA);
    const [expanded, setExpanded] = useState({});
    const [summary, setSummary] = useState(null);
    const [details, setDetails] = useState(null);
    const [level, setLevel] = useState(DEFAULT_LEVEL);
    // Period preset (which start/end months are auto-filled). Pure UX state; the
    // months themselves live on `criteria` because they go to the backend.
    const [periodPreset, setPeriodPreset] = useState(DEFAULT_PERIOD_PRESET);
    // Column preset + the derived visible-columns map. The tables consume
    // `visibleColumns` to conditionally render supplementary columns, and both
    // are sent with every report request (see buildRequestData): the backend
    // stores them with the generated report so a re-opened report renders the
    // columns it was generated with, and hashes them into the preview cache key
    // so two different column selections never share a cached report.
    const [columnPreset, setColumnPresetState] = useState(DEFAULT_COLUMN_PRESET);
    const [visibleColumns, setVisibleColumns] = useState(columnsForPreset(DEFAULT_COLUMN_PRESET));
    // "Hide rows with no values" — on by default.
    const [hideEmptyRows, setHideEmptyRows] = useState(true);
    // Both "Department" and "Department + Cost Centre" are summary reports; only
    // "Full details" is detailed. viewType drives which table renders and which
    // cache slot (report_type) is used.
    const viewType = levelToReportType(level);
    const [isGenerating, setIsGenerating] = useState({ summary: false, detailed: false });
    const [generationTime, setGenerationTime] = useState({ summary: null, detailed: null });
    const [loadingCached, setLoadingCached] = useState({ summary: false, detailed: false });

    const isFetchingRef = useRef(false);
    const pendingCacheRequests = useRef({ summary: false, detailed: false });

    const toast = useToast();

    useEffect(() => {
        fetchConfigs();
    }, []);

    const toggleExpansion = React.useCallback(
        (type, ...keys) => {
            const [department, costcenter, code, workforce, project] = keys;
            const options = { ...expanded };

            if (code) {
                if (costcenter) {
                    if (project) {
                        options[type][department].costcenters[costcenter].projects[project].codes[code].expanded =
                            !options[type][department].costcenters[costcenter].projects[project].codes[code].expanded;
                    } else {
                        options[type][department].costcenters[costcenter].codes[code].expanded =
                            !options[type][department].costcenters[costcenter].codes[code].expanded;
                    }
                } else {
                    if (project) {
                        options[type][department].projects[project].codes[code].expanded = !options[type][department].projects[project].codes[code].expanded;
                    } else {
                        options[type][department].codes[code].expanded = !options[type][department].codes[code].expanded;
                    }
                }

                options[type][department][costcenter].expanded = !options[type][department][costcenter].expanded;
            } else if (costcenter) {
                if (workforce) {
                    options[type][department].costcenters[costcenter].workforce.expanded =
                        !options[type][department].costcenters[costcenter].workforce.expanded;
                } else {
                    if (project) {
                        options[type][department].costcenters[costcenter].projects[project].expanded =
                            !options[type][department].costcenters[costcenter].projects[project].expanded;
                    } else {
                        options[type][department].costcenters[costcenter].expanded = !options[type][department].costcenters[costcenter].expanded;
                    }
                }
            } else if (department) {
                if (workforce) {
                    options[type][department].workforce.expanded = !options[type][department].workforce.expanded;
                } else {
                    if (project) {
                        options[type][department].projects[project].expanded = !options[type][department].projects[project].expanded;
                    } else {
                        options[type][department].expanded = !options[type][department].expanded;
                    }
                }
            }

            setExpanded(options);
        },
        [expanded],
    );

    const toggleAllExpansion = React.useCallback(
        (expand = true) => {
            const options = JSON.parse(JSON.stringify(expanded));

            Object.entries(options.expense).forEach(([expenseKey, expenseValue]) => {
                options.expense[expenseKey].expanded = expand;

                Object.entries(expenseValue.projects).forEach(([projectKey, projectValue]) => {
                    options.expense[expenseKey].projects[projectKey].expanded = expand;

                    Object.entries(projectValue.codes).forEach(([projectCodeKey, projectCodeValue]) => {
                        options.expense[expenseKey].projects[projectKey].codes[projectCodeKey].expanded = expand;
                    });
                });

                Object.entries(expenseValue.codes).forEach(([codeKey, codeValue]) => {
                    options.expense[expenseKey].codes[codeKey].expanded = expand;
                });

                Object.entries(expenseValue.costcenters).forEach(([costcenterKey, costcenterValue]) => {
                    options.expense[expenseKey].costcenters[costcenterKey].expanded = expand;

                    Object.entries(costcenterValue.projects).forEach(([projectCcKey, projectCcValue]) => {
                        options.expense[expenseKey].costcenters[costcenterKey].projects[projectCcKey].expanded = expand;

                        Object.entries(projectCcValue.codes).forEach(([projectCcCodeKey, projectCcCodeValue]) => {
                            options.expense[expenseKey].costcenters[costcenterKey].projects[projectCcKey].codes[projectCcCodeKey].expanded = expand;
                        });
                    });

                    Object.entries(costcenterValue.codes).forEach(([codeKey, codeValue]) => {
                        options.expense[expenseKey].costcenters[costcenterKey].codes[codeKey].expanded = expand;
                    });
                });
            });

            Object.entries(options.revenue).forEach(([revenueKey, revenueValue]) => {
                options.revenue[revenueKey].expanded = expand;

                Object.entries(revenueValue.codes).forEach(([codeKey, codeValue]) => {
                    options.revenue[revenueKey].codes[codeKey].expanded = expand;
                });

                Object.entries(revenueValue.costcenters).forEach(([costcenterKey, costcenterValue]) => {
                    options.revenue[revenueKey].costcenters[costcenterKey].expanded = expand;

                    Object.entries(costcenterValue.codes).forEach(([codeKey, codeValue]) => {
                        options.revenue[revenueKey].costcenters[costcenterKey].codes[codeKey].expanded = expand;
                    });
                });
            });

            setExpanded(options);
        },
        [expanded],
    );

    const updateCriteria = (newCriteria) => {
        setSummary(null);
        setDetails(null);
        setCriteria(newCriteria);

        return generateReport(newCriteria);
    };

    const updateLevel = (newLevel) => {
        setLevel(newLevel);
    };

    // Switching the column preset resets any per-column overrides to that
    // preset's defaults.
    const setColumnPreset = useCallback((preset) => {
        setColumnPresetState(preset);
        setVisibleColumns(columnsForPreset(preset));
    }, []);

    // Toggle a single supplementary column (a "Customise columns" override).
    const setColumnVisibility = useCallback((columnKey, visible) => {
        setVisibleColumns((prev) => ({ ...prev, [columnKey]: visible }));
    }, []);

    const fetchConfigs = () => {
        apiClient
            .get(fetchConfigsUrl)
            .then(({ data }) => {
                setConfigs(data);
                setCriteria((state) => ({
                    ...state,
                    budget_id: data.currentBudget.id,
                    // Default period is "Full project": snap to whole months so
                    // the month pickers and preset math never see partial months.
                    start_date: format(startOfMonth(new Date(data.minDate)), 'yyyy-MM-dd'),
                    end_date: format(endOfMonth(new Date(data.maxDate)), 'yyyy-MM-dd'),
                }));
            })
            .catch((error) => {
                console.log(error);
            });
    };

    // Build request data from criteria
    const buildRequestData = useCallback(
        (criteriaData, reportType) => {
            const data = {
                reference_type: criteriaData.format,
                budget_id: criteriaData.budget_id,
                start_date: criteriaData.start_date,
                end_date: criteriaData.end_date,
                // Workforce details are only available at Full details.
                show_workforce_details: level === LEVEL_FULL_DETAILS ? criteriaData.show_workforce_details : false,
                // IB-834: unallocated/unreconciled inclusion. Default ON when unset.
                include_unallocated: criteriaData.include_unallocated !== false,
                name: criteriaData.name,
                collapse_options: expanded,
                report_type: reportType,
                report_level: level,
                // Display-only, but they must travel: they decide which columns
                // the generated report renders, and they are part of the preview
                // cache's parameter hash.
                column_preset: columnPreset,
                visible_columns: visibleColumns,
            };

            if (criteriaData.departments && criteriaData.departments.length > 0) {
                data['departments'] = criteriaData.departments;
            }

            if (criteriaData.costcenters && Object.keys(criteriaData.costcenters).length > 0) {
                data['costcenters'] = criteriaData.costcenters;
            }

            // Only send a comparison budget when one is chosen; omitting the key
            // means "no comparison" and keeps the cache slot identical to today.
            if (criteriaData.comparison_budget_id) {
                data['comparison_budget_id'] = criteriaData.comparison_budget_id;
            }

            return data;
        },
        [expanded, level, columnPreset, visibleColumns],
    );

    // Fetch cached report for a specific type
    const fetchCachedReport = useCallback(
        async (criteriaData, reportType) => {
            if (!previewCachedUrl) {
                // Fallback to direct fetch if cached URL not available
                return null;
            }

            // Prevent duplicate requests for the same report type
            if (pendingCacheRequests.current[reportType]) {
                return null;
            }
            pendingCacheRequests.current[reportType] = true;

            const data = buildRequestData(criteriaData, reportType);
            setLoadingCached((prev) => ({ ...prev, [reportType]: true }));

            try {
                const response = await apiClient.post(previewCachedUrl, data);
                const result = response.data;

                if (result.status === 'cached' && result.data) {
                    setGenerationTime((prev) => ({ ...prev, [reportType]: result.generation_time }));

                    if (reportType === 'summary' && result.data.summary) {
                        setSummary(result.data.summary);
                        return true;
                    } else if (reportType === 'detailed' && result.data.data) {
                        const { headings, data: reportData, dates, collapse_options } = result.data;
                        if (collapse_options) {
                            setExpanded(collapse_options);
                        }
                        const datesObj = {};
                        if (dates) {
                            dates.forEach((date) => {
                                datesObj[date] = details?.dates && details.dates[date] !== undefined ? details.dates[date] : true;
                            });
                        }
                        setDetails({
                            datesArray: dates || [],
                            dates: datesObj,
                            headings: headings || [],
                            data: reportData,
                        });
                        return true;
                    }
                }
                return false;
            } catch (e) {
                console.log('Error fetching cached report:', e);
                return false;
            } finally {
                pendingCacheRequests.current[reportType] = false;
                setLoadingCached((prev) => ({ ...prev, [reportType]: false }));
                setIsGenerating((prev) => ({ ...prev, [reportType]: false }));
            }
        },
        [buildRequestData, details, previewCachedUrl],
    );

    const generateReport = useCallback(
        (criteriaData) => {
            const data = buildRequestData(criteriaData, viewType);

            return apiClient
                .post(generateReportUrl, data)
                .then((response) => {
                    if (onReportGenerated) {
                        onReportGenerated();
                    } else {
                        const redirectUrl = new URL(actualCostsCreateUrl);
                        redirectUrl.searchParams.set('tab', 'reports');
                        window.location.href = redirectUrl.toString();
                    }
                })
                .catch((error) => {
                    toast.error($t('An error occurred while generating the report. Please try again.'));
                });
        },
        [buildRequestData, actualCostsCreateUrl, generateReportUrl, toast, viewType, onReportGenerated],
    );

    // Trigger async report generation
    const triggerReportGeneration = useCallback(
        async (criteriaData, reportType) => {
            const data = buildRequestData(criteriaData, reportType);
            setIsGenerating((prev) => ({ ...prev, [reportType]: true }));

            try {
                const response = await apiClient.post(previewRefreshUrl, data);
                const result = response.data;

                // If already generating, keep the generating state
                if (result.status === 'already_generating') {
                    return result;
                }

                // Report generation has been queued
                // isGenerating stays true until notification clears it via clearGeneratingState
                return result;
            } catch (e) {
                setIsGenerating((prev) => ({ ...prev, [reportType]: false }));
                console.log('Error triggering report generation:', e);
                throw e;
            }
        },
        [buildRequestData, previewRefreshUrl],
    );

    // Refresh report - triggers new generation
    const refreshReport = useCallback(
        async (reportType) => {
            return triggerReportGeneration(criteria, reportType);
        },
        [criteria, triggerReportGeneration],
    );

    // Clear generation state (called when notification received)
    const clearGeneratingState = useCallback((reportType) => {
        setIsGenerating((prev) => ({ ...prev, [reportType]: false }));
    }, []);

    // Fetch a single report type with cache - tries cached first, then triggers generation or falls back to direct fetch
    const fetchSingleWithCache = useCallback(
        async (criteriaData, data, reportType) => {
            const typedData = { ...data, report_type: reportType };

            const result = await fetchCachedReport(criteriaData, reportType).then((cached) => {
                if (!cached) {
                    return triggerReportGeneration(criteriaData, reportType);
                }
                return { status: 'cached' };
            });

            if (result && typeof result === 'object' && (result.status === 'generating' || result.status === 'already_generating')) {
                toast.info($t('Report generation has been queued. You will be notified when it is ready.'));
            }

            return result;
        },
        [fetchCachedReport, triggerReportGeneration, toast],
    );

    // Fetch report for a single type - uses cache flow if available, otherwise direct fetch
    const fetchReportForType = useCallback(
        (criteriaData, reportType) => {
            const data = {
                reference_type: criteriaData.format,
                budget_id: criteriaData.budget_id,
                start_date: criteriaData.start_date,
                end_date: criteriaData.end_date,
                show_workforce_details: criteriaData.show_workforce_details,
                name: criteriaData.name,
                collapse_options: expanded,
                // Same reason as buildRequestData: the column set is part of the
                // cache's parameter hash, so a payload that omits it would look
                // up a different cache slot than the one the report was stored in.
                column_preset: columnPreset,
                visible_columns: visibleColumns,
            };

            if (criteriaData.departments && criteriaData.departments.length > 0) {
                data['departments'] = criteriaData.departments;
            }

            if (criteriaData.costcenters && Object.keys(criteriaData.costcenters).length > 0) {
                data['costcenters'] = criteriaData.costcenters;
            }

            if (previewCachedUrl) {
                return fetchSingleWithCache(criteriaData, data, reportType);
            }
        },
        [expanded, previewCachedUrl, fetchSingleWithCache, columnPreset, visibleColumns],
    );

    const fetchPreview = (criteria) => {
        // Prevent duplicate fetch requests using ref for synchronous check
        if (isFetchingRef.current) {
            return Promise.resolve();
        }

        isFetchingRef.current = true;

        // Only fetch the currently selected view type
        return fetchReportForType(criteria, viewType).finally(() => {
            isFetchingRef.current = false;
        });
    };

    const value = {
        module,
        details,
        summary,
        configs,
        expanded,
        criteria,
        viewType,
        level,
        reportConfigsIndexUrl,
        reportConfigsStoreUrl,
        reportConfigsDeleteUrl,
        periodPreset,
        columnPreset,
        visibleColumns,
        hideEmptyRows,
        isGenerating,
        generationTime,
        loadingCached,
        toggleExpansion,
        toggleAllExpansion,
        updateCriteria,
        updateLevel,
        setPeriodPreset,
        setColumnPreset,
        setColumnVisibility,
        setHideEmptyRows,
        refreshReport,
        fetchCachedReport,
        clearGeneratingState,
    };

    return <ActualVsBudgetReportContext.Provider value={value}>{children}</ActualVsBudgetReportContext.Provider>;
};

export default ActualVsBudgetReportContext;
